import React, { useState, useEffect } from "react";
import { Empty, Spin, Tooltip, Popconfirm, message } from "antd";
import { DeleteOutlined, BookOutlined } from "@ant-design/icons";
import { getSavedViews, deleteSavedView } from "Explore/Services/SavedViews";
import moment from "moment";
import styles from "./SavedViewsList.module.scss";

const SavedViewsList = ({ onRestoreView, refreshTrigger }) => {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchViews = async () => {
    setLoading(true);
    try {
      const data = await getSavedViews();
      setViews(data?.items || []);
    } catch (error) {
      console.error("Failed to fetch saved views:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchViews();
  }, [refreshTrigger]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteSavedView(id);
      message.success("Saved view deleted");
      setViews(views.filter(v => v.id !== id));
    } catch (error) {
      message.error("Failed to delete saved view");
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "40px" }}><Spin /></div>;
  }

  if (views.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No saved views yet."
        />
      </div>
    );
  }

  return (
    <div className={styles.savedViewsList}>
      {views.map(view => (
        <div 
          key={view.id} 
          className={styles.viewCard}
          onClick={() => onRestoreView(view)}
        >
          {view.entity_avatar ? (
            <div className={styles.avatar}>
              <img src={view.entity_avatar} alt={view.entity_name} />
            </div>
          ) : (
             <div className={styles.avatarPlaceholder}>
                <BookOutlined />
             </div>
          )}
          <div className={styles.viewInfo}>
            <div className={styles.viewName}>{view.name}</div>
            <div className={styles.viewDate}>
              {(() => {
                const rawDate = view.created_at || view.createdAt || view.updated_at || view.updatedAt || view.timestamp;
                if (!rawDate) return "Unknown date";
                
                // Backend sends non-standard ISO string like "2026-08-14T07:48:19.008703+00:00Z"
                const dateVal = typeof rawDate === 'string' ? rawDate.replace('+00:00Z', 'Z') : rawDate;
                
                let m = moment(dateVal);
                if (!m.isValid() && !isNaN(Number(dateVal))) {
                  m = moment(Number(dateVal));
                }
                if (m.isValid() && m.year() === 1970 && Number(dateVal) < 2000000000) {
                  m = moment(Number(dateVal) * 1000);
                }
                
                if (m.isValid()) return m.fromNow();
                return String(dateVal); // Render raw if still invalid so we can see it
              })()} • {view.entity_name}
            </div>
          </div>
          <div className={styles.actions}>
            <Popconfirm
              title="Delete saved view?"
              onConfirm={(e) => handleDelete(e, view.id)}
              onCancel={(e) => e.stopPropagation()}
              okText="Yes"
              cancelText="No"
              placement="left"
            >
              <div 
                className={styles.deleteBtn} 
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Delete view">
                  <DeleteOutlined />
                </Tooltip>
              </div>
            </Popconfirm>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SavedViewsList;
